import { Link, useLocation } from "react-router-dom";
import { Instagram } from "lucide-react";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";
import { useMemo, useEffect } from "react";
import { writeCart } from "../features/cart/cart";
import { track } from "../features/analytics/tracker";

// Helper to map the slot ID to a friendly name
const SLOT_NAMES = {
  morning: "Late Morning (10:30am – 1:00pm)",
  evening: "Early Evening (5:30pm – 7:00pm)",
  late:    "Late Evening (7:00pm – 9:00pm)",
};

export default function SuccessPage() {
  usePageMeta({ title: 'Order Confirmed | TearDrop Hydro', path: '/success', noindex: true });
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);
  
  // Retrieve the total and the slot before they are cleared from memory
  const lastTotal = typeof window !== "undefined" ? sessionStorage.getItem("td:lastCartTotal") : "";
  // Note: You should update your CartPage to save 'td:lastDeliverySlot' to sessionStorage before redirecting
  const lastSlot = typeof window !== "undefined" ? sessionStorage.getItem("td:lastDeliverySlot") : "";

  useEffect(() => {
    const finalise = async () => {
      sessionStorage.removeItem("td:checkoutHold"); // paid: the hold is now the order
      const total = sessionStorage.getItem("td:lastCartTotal");
      const sessionId = qs.get("session_id");
      // Discount codes are recorded by the server when the order is placed or
      // paid, so there is nothing to do for them here.

      track("order_completed", { total, sessionId, slot: lastSlot });

      // 🧹 Clean up
      writeCart([]);
      sessionStorage.removeItem("td:lastCartTotal");
      sessionStorage.removeItem("td:lastDeliverySlot"); // Clean up slot too
    };
    finalise();
  }, [qs, lastSlot]);

  return (
    <Layout>
      <div className="container section" style={{ maxWidth: 720 }}>
        <div className="card" style={{ padding: 24, margin: 10, cursor: "default" }}>
          <h1 className="h2" style={{ marginTop: 0 }}>Order received, thank you! 🎉</h1>
          <p className="lead">
            Your greens are as good as picked. We’ll send a confirmation email shortly.
          </p>

          {/* 🚚 Confirmed Delivery Window */}
          <div
            style={{
              background: "var(--brand-2-light, #f0fff0)",
              border: "1px solid var(--brand-2, #4CAF50)",
              padding: 16,
              borderRadius: 8,
              marginTop: 16,
              marginBottom: 16
            }}
          >
            <h3 style={{ marginTop: 0, color: "#2d5a27", fontSize: 18 }}>
              Confirmed Delivery Window
            </h3>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>
              {SLOT_NAMES[lastSlot] || "Standard Delivery Schedule"}
            </p>
            <p className="small" style={{ marginTop: 8, marginBottom: 0, color: "#444" }}>
              Michael will deliver your harvest fresh at your chosen delivery time. 
              {lastSlot === 'late' && " Thank you for the late delivery request!"}
            </p>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            {lastTotal && (
              <p className="muted">
                Payment total: <strong>£{Number(lastTotal).toFixed(2)}</strong>
              </p>
            )}
            {qs.get("session_id") && (
              <p className="muted small">Ref: <code>{qs.get("session_id").slice(-8)}</code></p>
            )}
          </div>

          <div className="cta-row" style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link className="btn btn-primary" to="/browse-products">Browse products</Link>
            <Link className="btn btn-ghost" to="/">Back to home</Link>
          </div>

          <p className="muted small" style={{ marginTop: 16 }}>
            <a
              href="https://www.instagram.com/teardrop_hydro/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              onClick={() => track("success_instagram_click")}
            >
              <Instagram size={15} />
              Tag us @teardrop_hydro when it lands!
            </a>
          </p>

          <hr style={{ margin: "24px 0" }} />

          <div className="muted small">
            <p style={{ marginTop: 0 }}>
              Questions? Reply to your confirmation email or call/text Michael at <a href="tel:07541264513">07541 264513</a>.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}